import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { UserData } from "../../../Context/DataContext";

/*
  The partner view.

  A partner sees the same application a customer does, plus a switch at the top
  between two contexts: their own customers, and a demo account they can show
  people. Only the demo side exists today.

  Turning that switch on moves the navigation. The tab row that normally sits in
  the blue header band gives up its place to the switcher, and the tabs reappear
  below the page title in blue-on-white.

  ---- why the tabs are not simply rendered by the header ----

  They have to sit AFTER the page title, and the title belongs to each page while
  the header is a sibling rendered BEFORE it. No amount of CSS reorders that,
  because the title is nested inside the page rather than being a sibling of it.

  So the strip has two homes. Pages that own a title render it through
  PageHeading, which puts the strip directly beneath. Every other page under
  /user -- Help Center, Contact Us, Inheritance Parameters -- gets it from the
  header instead, above the content rather than below a title. Those pages are
  not in the design, and the alternative is a partner stranded with no
  navigation at all, which is worse than a strip in a slightly different place.
*/

/*
  The customer side of the switcher. Its page draws its own title and, per the
  design, no tab strip -- the tabs belong to the demo account.
*/
export const CUSTOMERS_PATH = "/user/customers";

/*
  Where the switcher sends you when you come back from the customer side.
*/
export const DEMO_PATH = "/user/dashboard";

/*
  The five destinations. Asset Management sits second, its Wealth Report
  directly behind it, then Beneficiaries and Trusted Advisors. "My Account" is
  deliberately absent: it lives in the header's own right-hand block, next to the
  avatar, exactly as it does today.

  Labels are the same DublicatedText keys the header's NavBar uses, so a partner
  and a customer never see two different words for one page.
*/
export const PARTNER_TABS = [
  { key: "dashboard", link: "/user/dashboard", labelKey: "dublicatedText80" },
  {
    key: "assetmanagement",
    link: "/user/assetmanagement",
    labelKey: "dublicatedText282",
  },
  /*
    The Wealth Report view of Asset Management, carried here so the demo
    account's strip matches the band a customer sees. Its own tab rather than
    only a crumb under the heading, for the same reason: the crumb is grey on
    white below the title and was being missed.
  */
  {
    key: "wealth",
    link: "/user/assetmanagement/dashboard",
    labelKey: "dublicatedText306",
  },
  {
    key: "beneficiaries",
    link: "/user/beneficiaries",
    labelKey: "dublicatedText82",
  },
  { key: "advisors", link: "/user/advisors", labelKey: "dublicatedText26" },
];

/*
  Paths whose page renders its own heading, so the header leaves them alone and
  the strip is never drawn twice. The customers page is here for a different
  reason -- it has a title but deliberately no tabs at all.

  /user/assetmanagement/dashboard is the Asset Dashboard, a second page behind
  the same tab -- it has a heading of its own, so it belongs here too.
*/
/*
  One customer's profile, opened from the list. Everything under this prefix --
  the profile and its own tabs -- draws its own heading, so the header keeps out
  of the way there too.
*/
export const customerPath = (id, tab) =>
  `${CUSTOMERS_PATH}/${id}${tab ? `/${tab}` : ""}`;

export const HEADING_PATHS = [
  CUSTOMERS_PATH,
  "/user/dashboard",
  "/user/beneficiaries",
  "/user/advisors",
  "/user/assetmanagement",
  "/user/assetmanagement/dashboard",
];

/*
  `dublicatedText26` is stored as "Trusted advisors - ", with a trailing dash the
  header also strips before displaying it. Same treatment here rather than a
  second copy of the string.
*/
export const tabLabel = (text, tab) =>
  String(text?.[tab.labelKey] || "")
    .replace(/\s*-\s*$/, "")
    .trim();

/*
  The tab standing for the current page: the longest link that prefixes the
  path. That rule is what lets the Wealth Report sit behind Asset Management
  without the two fighting -- /user/assetmanagement/dashboard matches both and
  the longer one wins, so each page lights its own tab.
  Returns null off the tabbed pages, which simply leaves none of them marked.
*/
export const activeTabLink = (pathname) =>
  PARTNER_TABS.map((t) => t.link)
    .filter((link) => pathname === link || pathname.startsWith(link + "/"))
    .reduce((best, link) => (link.length > (best || "").length ? link : best), null);

/*
  True only for a partner, and only inside the application. Everything else --
  the public site, the admin console, the beneficiary portal, and every other
  user type -- is left exactly as it is.

  `user_type` is read from the CustomerUser document (Context/DataContext's
  /user/customer call), not from `auth.userType`: that one comes from
  req.session.type, which assignOTPpermissions never sets.
*/
export const usePartnerView = () => {
  const customer = UserData();
  const { pathname } = useLocation();
  return (
    customer?.CustomerUser?.user_type === "Partner" &&
    pathname.startsWith("/user")
  );
};

/*
  Whether this partner may manage customers at all.

  Set by an admin on the create/edit partner form and served by
  /user/partner/info. False -- which is the default -- means the "My Customer"
  half of the switcher does not exist for them: they get only the demo account.

  Undefined while that request is still in flight, which is treated as false so
  the tab never flickers into view for someone who should not have it.
*/
export const useCanManageCustomers = () => {
  const customer = UserData();
  return customer?.userValidationData?.can_manage_customers === true;
};

/*
  True where the page supplies its own title, and the header should not draw the
  tab strip. An exact list is not enough on its own: a customer profile lives at
  a path built from an id, so it is matched by prefix.
*/
export const drawsOwnHeading = (pathname) =>
  HEADING_PATHS.includes(pathname) || pathname.startsWith(CUSTOMERS_PATH + "/");

/* True anywhere on the customer side, list or profile. */
export const onCustomerSide = (pathname) =>
  pathname === CUSTOMERS_PATH || pathname.startsWith(CUSTOMERS_PATH + "/");

/*
  True while a page is being shown as part of a customer's profile.

  Those pages are guests there: the profile already supplies the heading (the
  customer's name) and the tabs, so each page must not draw its own on top. Kept
  separate from usePartnerView, which still has to be true on these paths -- the
  header needs its switcher and the profile needs its guard.
*/
export const useInCustomerProfile = () => {
  const { pathname } = useLocation();
  return pathname.startsWith(CUSTOMERS_PATH + "/");
};

/*
  Where a page shown inside a customer's profile puts its own action button.

  The profile owns the top of the screen -- "Back to customer List" on the left,
  the customer's name in the middle -- and a guest page's button belongs on that
  same line, on the right. But the two live in different components with no
  shared positioning ancestor, so the guest cannot reach that line with CSS: its
  own header sits far below, under the tabs and the crumbs.

  So the profile renders an empty slot there and the guest portals into it. An
  id rather than a context because the profile is a single page with a single
  header; two of them on screen at once is not a state this app has.
*/
export const CUSTOMER_HEADER_ACTIONS_ID = "customer_header_actions";

/*
  The slot node, once it exists. Null on the first render -- the profile mounts
  its header in the same pass -- so callers keep their own fallback for that
  frame rather than dropping the button.
*/
export const useCustomerHeaderSlot = (enabled) => {
  const [node, setNode] = useState(null);
  /*
    Re-checked after every render, not once on mount: the profile swaps its
    whole tree between loading and loaded, and a node cached across that would
    be detached -- the button would portal into nothing and disappear. The
    lookup is a getElementById, and setState bails out when the answer has not
    changed, so this settles immediately and costs nothing after that.
    No dependency list, and eslint reads that as an infinite loop -- normally it
    would be. The functional update returning `current` unchanged is what stops
    it: React bails out instead of re-rendering. Counted in
    partnerNavRender.test.jsx rather than left as a claim.
  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const found = enabled
      ? document.getElementById(CUSTOMER_HEADER_ACTIONS_ID)
      : null;
    setNode((current) => (current === found ? current : found));
  });
  return node;
};
